import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, retry, throwError, timer } from "rxjs";
import { AuthService } from "../services/auth.service";
import { AlertService } from "../services/alert.service";
import { environment } from "src/environments/environment";
import { SKIP_GLOBAL_ERROR_ALERT } from "./http-error-context";

let handlingUnauthorized = false;

export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const alertService = inject(AlertService);
  const backendApiEndpoint = normalizeBaseUrl(environment.apiEndpoint);

  return next(req).pipe(
    retry({
      count: 2,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        const retryableStatuses = [0, 500, 502, 503, 504];
        const canRetry =
          req.method === "GET" && retryableStatuses.includes(error.status);
        return canRetry ? timer(300 * retryCount) : throwError(() => error);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      const esBackendApi = isBackendApiRequest(req.url, backendApiEndpoint);
      if (!esBackendApi) {
        return throwError(() => error);
      }
      if (req.context.get(SKIP_GLOBAL_ERROR_ALERT) && error.status !== 401) {
        return throwError(() => error);
      }
      return handleBackendError(error, authService, router, alertService, req);
    }),
  );
};

function normalizeBaseUrl(url: string): string {
  const value = String(url || "").trim();
  if (!value) return "/";
  return value.endsWith("/") ? value : `${value}/`;
}

function isBackendApiRequest(url: string, backendApiEndpoint: string): boolean {
  return url.startsWith(backendApiEndpoint) || url.includes("/api/");
}

/*  Manejo de errores para BACKEND
 */
function handleBackendError(
  error: HttpErrorResponse,
  authService: AuthService,
  router: Router,
  alertService: AlertService,
  req: any,
): ReturnType<typeof throwError> {
  console.error(" Error HTTP:", {
    status: error.status,
    url: req.urlWithParams ?? req.url,
    error: error.error,
  });

  // El login traduce sus propios errores y evita exponer mensajes tecnicos del backend.
  if (isLoginRequest(req.url)) {
    return throwError(() => error);
  }

  // ERROR 401: Sesión expirada o no autorizado
  if (error.status === 401) {
    return handle401Error(error, authService, router, alertService, req);
  }

  // ERROR 403: Acceso denegado
  if (error.status === 403) {
    return handle403Error(error, alertService);
  }

  // ERROR 404: Recurso no encontrado
  if (error.status === 404) {
    return throwError(() => error);
  }

  // ERROR 400: Solicitud incorrecta
  if (error.status === 400) {
    return handle400Error(error, alertService, req);
  }

  // ERROR 500: Error del servidor
  if (error.status === 500) {
    return handle500Error(error, alertService, req);
  }

  // ERROR 503: Servicio no disponible
  if (error.status === 503) {
    return handle503Error(error, alertService);
  }

  // Errores de red (sin conexión)
  if (error.error instanceof ErrorEvent || error.status === 0) {
    return handleNetworkError(error, alertService);
  }

  // Otros errores no clasificados
  return handleGenericError(error, alertService);
}

// FUNCIONES AUXILIARES PARA BACKEND REAL

function handle401Error(
  error: HttpErrorResponse,
  authService: AuthService,
  router: Router,
  alertService: AlertService,
  req: any,
): ReturnType<typeof throwError> {
  if (handlingUnauthorized) {
    return throwError(() => error);
  }

  handlingUnauthorized = true;
  const teniaToken = authService.getToken() !== null;

  authService.clearAuth();
  void router
    .navigate(["/authentication/login"])
    .then(async () => {
      if (teniaToken) {
        await alertService.mostrarAdvertencia(
          "Su sesión ha expirado. Por favor, inicie sesión nuevamente.",
          "Sesión expirada",
        );
      }
    })
    .finally(() => {
      handlingUnauthorized = false;
    });
  return throwError(() => error);
}

function handle403Error(
  error: HttpErrorResponse,
  alertService: AlertService,
): ReturnType<typeof throwError> {
  const errorMessage =
    error.error?.message || "No tiene permisos para realizar esta acción.";
  alertService.mostrarError(errorMessage, "Acceso denegado");
  return throwError(() => error);
}

function handle400Error(
  error: HttpErrorResponse,
  alertService: AlertService,
  req: any,
): ReturnType<typeof throwError> {
  let errorMessage = "Solicitud incorrecta. Verifique los datos.";

  if (error.error?.message) {
    errorMessage = error.error.message;
  } else if (typeof error.error === "string") {
    errorMessage = error.error;
  }

  alertService.toastError(errorMessage);
  return throwError(() => error);
}

function isLoginRequest(url: string): boolean {
  return url.includes("/auth/login");
}

function handle500Error(
  error: HttpErrorResponse,
  alertService: AlertService,
  req: any,
): ReturnType<typeof throwError> {
  alertService.mostrarError(
    "Error interno del servidor. Por favor, intente más tarde o contacte al administrador.",
    "Error del servidor",
  );
  return throwError(() => error);
}

function handle503Error(
  error: HttpErrorResponse,
  alertService: AlertService,
): ReturnType<typeof throwError> {
  alertService.mostrarError(
    "El servicio no está disponible en este momento. Intente más tarde.",
    "Servicio no disponible",
  );
  return throwError(() => error);
}

function handleNetworkError(
  error: HttpErrorResponse,
  alertService: AlertService,
): ReturnType<typeof throwError> {
  alertService.mostrarError(
    "No se puede conectar con el servidor. Verifique su conexión a internet.",
    "Error de conexión",
  );
  return throwError(() => error);
}

function handleGenericError(
  error: HttpErrorResponse,
  alertService: AlertService,
): ReturnType<typeof throwError> {
  const errorMessage =
    error.error?.message || "Ha ocurrido un error inesperado.";
  alertService.toastError(errorMessage);
  return throwError(() => error);
}
